<template lang="pug">
#reservation-schedule-table(v-cloak)
  .reservation-schedule-table(:style="{ height: height}")
    .thead(v-show="!isLoading")
      .header-line
        .th.hour 時刻
        .th.date.flex-fill.d-flex.flex-column(v-for="header in createDateHeaders()" :id="'online-' + header.date")
          .date-num {{ header.date }}
          .date-week
            span （
            span(:class="header.cssClass") {{ header.week }}
            span ）
          b-popover(v-if="header.holidayName" :target="'online-' + header.date" triggers="hover")
            span {{ header.holidayName }}
    .tbody
      // 08:00から表示したいので、08:00～23:00までを先にループで回す
      // v-forのオブジェクトではない、回数ループの場合はiは初期値1固定になってしまうので、0始まりにするために-1して調整
      template(v-for="i in 40")
        template(v-if="showRecord(_value[i - 1])")
          .body-line
            // 時間列
            .td.hour.justify-content-center {{ hourZeroPadding(_value[i - 1]) }}:{{ minZeroPadding(_value[i - 1]) }} ～
            // 日付列
            .td.date.flex-fill.justify-content-center.align-items-center(v-for="(date, dateIndex) in _value[i - 1]"
              @click="toggleCanReservation(i - 1, dateIndex)")
              .d-flex.flex-column
                span.can(v-if="date.canReservation" :class="{ 'is-reserved': 0 < date.reservationRequestsCount }") 〇
                span.cant(v-else :class="{ 'is-reserved': 0 < date.reservationRequestsCount }") ×
                span.reserved(v-if="0 < date.reservationRequestsCount") 予約有

    .loading(v-show="isLoading")
      .loading-msg
        b-spinner.spinner
        span.message.align-self-center 読込み中...
</template>

<script lang="ts">
import { Inject, Component, Prop, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import OnlineReservationSettingsService, { OnlineReservationSettingsGetModel } from "@/commons/services/OnlineReservationSettings";

import { nationalHolidaysStore } from "@/store/store";
import { NationalHolidaysGetModel } from "@/commons/services/NationalHolidays";

import { ReservationScheduleModel } from "@/pc/containers/reservations/ReservationScheduleTab.vue";
import Loading from "@/pc/basics/Loading.vue";

/**
 * オンライン面会スケジュールテーブルコンポーネント
 */
@Component({
  components: {
    Loading
  }
})
export default class OnlineReservationScheduleStatusTable extends Vue {
  /** オンライン面会設定サービス */
  @Inject("OnlineReservationSettingsService") onlineReservationSettingsService!: OnlineReservationSettingsService;

  /** 表示対象のオンライン面会スケジュール */
  @Prop({
    required: true
  })
  value!: Array<Array<ReservationScheduleModel>>;

  @Prop({
    required: true
  })
  targetDate!: Date;

  @Prop({
    required: true
  })
  isLoading!: boolean;

  /** テーブル高さ */
  @Prop({
    required: true
  })
  height!: string;

  /** 祝日MAP */
  holidaysMap: Map<string, NationalHolidaysGetModel> = new Map();

  /** オンライン面会予約設定 */
  setting: OnlineReservationSettingsGetModel | any = {};

  get _value(): Array<Array<ReservationScheduleModel>> {
    return this.value;
  }

  /**
   * key: 曜日名
   * value: スタイルクラス名
   * 曜日毎に文字色を変えるために用いる
   */
  readonly Weeks: Map<string, string> = new Map([
    ["日", "sunday"],
    ["月", ""],
    ["火", ""],
    ["水", ""],
    ["木", ""],
    ["金", ""],
    ["土", "saturday"]
  ]);

  /**
   * vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    this.holidaysMap = this.createHolidaysMap(nationalHolidaysStore.holidays);
    await this.createView();
    return;
  }

  public createHolidaysMap(holidays: Array<NationalHolidaysGetModel>): Map<string, NationalHolidaysGetModel> {
    const holidaysMap: Map<string, NationalHolidaysGetModel> = new Map();
    for (const holiday of holidays) {
      holidaysMap.set(holiday.date, holiday);
    }
    return holidaysMap;
  }

  public async createView(): Promise<void> {
    // 時間あたりの表示する情報単位を調べるためにオンライン面会設定を取得する
    // 時間あたり何レコード必要か計算する
    const setting: OnlineReservationSettingsGetModel = await this.getOnlineReservationSetting();
    this.setting = setting;
  }

  /**
   * ### オンライン面会設定を取得する
   */
  public async getOnlineReservationSetting(): Promise<OnlineReservationSettingsGetModel> {
    const response: OnlineReservationSettingsGetModel = await this.onlineReservationSettingsService.get(userStore);
    return response;
  }

  /**
   * ### 日付情報の配列を返す
   *
   * - 対象年月が存在しない場合は空配列を返す
   */
  public createDateHeaders(): Array<{ week: string; date: string; cssClass: string }> {
    if (!this.targetDate) {
      return [];
    }

    // 表示対象年月から最終日を求める
    let tempDate: Date = new Date(this.targetDate.getTime());
    tempDate.setMonth(tempDate.getMonth() + 1);
    tempDate.setDate(1);
    tempDate.setDate(tempDate.getDate() - 1);
    const lastDate: number = tempDate.getDate();

    // ヘッダー用情報を作成する
    let dateHeaders: Array<{ week: string; date: string; cssClass: string; holidayName: string }> = [];
    for (let i = 0; i < lastDate; i++) {
      tempDate.setDate(i + 1);
      const week: string = this.$dateUtil.getWeekName(tempDate, "ja");
      const date: string = this.$dateUtil.convertDateToString(tempDate, "mm/dd", false);
      const cssClass: string | undefined = this.Weeks.get(week);
      const holidayName: NationalHolidaysGetModel | undefined = this.holidaysMap.get(this.$dateUtil.convertDateToString(tempDate, "yyyy-mm-dd"));

      dateHeaders.push({
        week,
        date,
        cssClass: holidayName ? "holiday" : cssClass ? cssClass : "",
        holidayName: holidayName ? holidayName.name : ""
      });
    }

    return dateHeaders;
  }

  /**
   * ### レコードを表示するかを返す
   */
  public showRecord(date: Array<ReservationScheduleModel>): boolean {
    const unit: number = this.setting.unit;

    if (date != undefined && date[0] != undefined) {
      const min: number = date[0].min;
      // 単位が1時間
      if (unit == 60 && min == 0) {
        return true;
      }
      // 単位が30分
      else if (unit == 30 && (min == 0 || min == 30)) {
        return true;
      }
      // 単位が15分
      else if (unit == 15) {
        return true;
      }
      // その他の場合は非表示
      else {
        return false;
      }
    } else {
      return true;
    }
  }

  /**
   * ### 1桁の場合に0埋めして2桁にする
   */
  public hourZeroPadding(date: Array<ReservationScheduleModel>): string {
    let converted: string = "";

    if (date != undefined && date[0] != undefined) {
      if (date[0].hour.toString().length == 1) {
        converted = "0" + date[0].hour.toString();
      } else if (date[0].hour.toString().length == 2) {
        converted = date[0].hour.toString();
      }
    }

    return converted;
  }

  /**
   * ### 1桁の場合に0埋めして2桁にする
   */
  public minZeroPadding(date: Array<ReservationScheduleModel>): string {
    let converted: string = "";

    if (date != undefined && date[0] != undefined) {
      if (date[0].min.toString().length == 1) {
        converted = "0" + date[0].min.toString();
      } else if (date[0].min.toString().length == 2) {
        converted = date[0].min.toString();
      }
    }

    return converted;
  }

  /**
   * ### 予約可否を変更できるかのCSSクラス名を返す
   */
  public getCanChangeCss(date: ReservationScheduleModel) {
    if (date.reservationRequestsCount != undefined && date.reservationRequestsCount <= 0) {
      return "can-change";
    } else {
      return "cant-change";
    }
  }

  /**
   * ### 予約可否を反転させる
   *
   * - 既にbotから面会希望が出されていた場合は勝手にキャンセルしてはいけない
   * - そのため、reservationRequestsCount(予約依頼数)が1件以上ある場合は反転させない
   */
  public toggleCanReservation(hourIndex: number, dateIndex: number): void {
    this.value[hourIndex][dateIndex].canReservation = !this.value[hourIndex][dateIndex].canReservation;
    this.$emit("change", this.value);
    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

#reservation-schedule-table {
  position: relative;

  .reservation-schedule-table {
    @include flex-table(2200px);
    margin-bottom: 10px;
    overflow-y: scroll;
    overflow-x: scroll;

    .thead {
      // headerはスクロールしても固定したいのでheader containerはstickyを指定
      position: sticky;
      top: 0;
      z-index: 200;

      // 縦横列固定なので、header行かつ、resident-name列はz-indexを一番大きくする
      .hour {
        min-height: 44px;
        position: sticky;
        left: 0;
        top: 0;
        z-index: 300;
      }
      // 回り込み時に下にある要素が見えてしまうので、背景色を指定
      .header-line > div {
        background-color: $bg-gray;
      }

      .th.hour {
        font-weight: normal;
      }

      .date {
        height: 44px;
        .date-week {
          font-weight: normal;
          font-size: 14px;
          line-height: 18px;
        }
        .date-num {
          font-weight: normal;
          font-size: 16px;
        }
        // 介護記録一覧画面では、強調する曜日の文字サイズは少し大きくする
        .saturday,
        .sunday {
          font-size: 16px;
        }
      }
    }

    .tbody {
      .hour {
        // 時間は固定したいのでstickyにする
        position: sticky;
        left: 0;
        z-index: 100;
      }
      .body-line {
        min-height: 40px;
        // 回り込み時に下にある要素が見えてしまうので、背景色を指定
        .td.hour {
          font-size: 14px;
          background-color: $bg-default;
        }
        &:nth-child(2n) > .td.hour {
          background-color: $bg-striped;
        }
        .td.date {
          height: 40px;
          .can {
            font-size: 20px;
            font-weight: bold;
            color: $font-key-color;
          }
          .cant {
            font-size: 20px;
            font-weight: bold;
            color: $font-disable-font-color;
          }
          .is-reserved {
            font-size: 16px;
            margin: -4px;
          }
          .reserved {
            font-size: 12px;
            color: $font-strong-color;
          }
          // 面会可能かどうかはリンクのように扱う
          &:hover {
            cursor: pointer;
            background-color: $btn-draggable-hover;
          }
        }
      }
    }

    .th,
    .td {
      &.date {
        text-align: center;
        width: 70px;
        min-width: 70px;
      }
      &.hour {
        width: 74px;
        min-width: 74px;
      }
    }
  }
  .loading {
    display: table;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background-color: #fff;
    opacity: 0.8;
    // ローディング表示中にヘッダーやサイドメニューを触らせたいためz-indexを調整
    // ヘッダー:z-index 1000
    // サイドメニュー：z-index 900
    z-index: 800;

    .loading-msg {
      display: table-cell;
      text-align: center;
      vertical-align: middle;
      .spinner {
        width: 40px;
        height: 40px;
        margin-right: 12px;
        color: $loading;
      }

      .message {
        @include bold-font(22px);
      }
    }
  }
}
</style>
